import {
  chakra,
  PopoverTrigger,
  Portal,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  useColorModeValue,
  DarkMode,
  ListItem,
  OrderedList,
  Box,
  VStack,
} from '@chakra-ui/react';
import React from 'react';

import type { FormFields } from '../types';
import type { SmartContractVerificationMethod, SmartContractVerificationConfig } from 'types/client/contract';

import useIsMobile from 'lib/hooks/useIsMobile';
import { useI18n } from 'lib/i18n/context';
import Popover from 'ui/shared/chakra/Popover';
import FormFieldFancySelect from 'ui/shared/forms/fields/FormFieldFancySelect';

import {
  CONTRACT_VERIFICATION_FIELD_KEYS,
  CONTRACT_VERIFICATION_METHOD_DESC_KEYS,
  CONTRACT_VERIFICATION_METHOD_LABEL_KEYS,
} from '../i18n';

/** Info circle from design — idle `#616161` in light & dark + hover */
const VerificationMethodsInfoIcon = () => {
  return (
    <chakra.svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="none"
      boxSize="20px"
      minW="20px"
      minH="20px"
      maxW="20px"
      maxH="20px"
      display="block"
      flexShrink={ 0 }
      color="#616161"
      transition="color 0.15s ease"
      _hover={{ color: '#14DCAC' }}
      aria-hidden
    >
      <path
        d={
          'M9.9974 18.3346C14.5807 18.3346 18.3307 14.5846 18.3307 10.0013C18.3307 5.41797 14.5807 1.66797 9.9974 1.66797' +
          'C5.41406 1.66797 1.66406 5.41797 1.66406 10.0013C1.66406 14.5846 5.41406 18.3346 9.9974 18.3346Z'
        }
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 6.66797V10.8346"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 13.332H10.0075"
        stroke="currentColor"
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </chakra.svg>
  );
};

interface Props {
  methods: SmartContractVerificationConfig['verification_options'];
}

const ContractVerificationFieldMethod = ({ methods }: Props) => {
  const { t } = useI18n();
  const tooltipBg = useColorModeValue('gray.700', 'gray.900');
  const titleColor = useColorModeValue('#000000', '#FFFFFF');
  const isMobile = useIsMobile();

  const options = React.useMemo(() => methods.map((method) => ({
    value: method,
    label: t(CONTRACT_VERIFICATION_METHOD_LABEL_KEYS[method]),
  })), [ methods, t ]);

  const renderPopoverListItem = React.useCallback((method: SmartContractVerificationMethod) => {
    return <ListItem key={ method }>{ t(CONTRACT_VERIFICATION_METHOD_DESC_KEYS[method]) }</ListItem>;
  }, [ t ]);

  return (
    <VStack align="stretch" spacing={{ base: 3, lg: 4 }}>
      <Box
        mt={{ base: 10, lg: 6 }}
        display="flex"
        flexDirection="row"
        flexWrap="nowrap"
        alignItems="flex-start"
        columnGap="6px"
      >
        <chakra.span
          flex="1"
          minW={ 0 }
          fontWeight={ 500 }
          fontSize="20px"
          lineHeight="24px"
          fontFamily="'Space Grotesk', sans-serif"
          color={ titleColor }
        >
          { t(CONTRACT_VERIFICATION_FIELD_KEYS.method.title, { count: methods.length }) }
        </chakra.span>
        <Popover trigger="hover" isLazy placement={ isMobile ? 'bottom-end' : 'right-start' } offset={ [ -8, 8 ] }>
          <PopoverTrigger>
            <chakra.span display="inline-flex" flexShrink={ 0 } cursor="pointer" alignItems="center" lineHeight={ 1 }>
              <VerificationMethodsInfoIcon/>
            </chakra.span>
          </PopoverTrigger>
          <Portal>
            <PopoverContent bgColor={ tooltipBg } w={{ base: '300px', lg: '380px' }}>
              <PopoverArrow bgColor={ tooltipBg }/>
              <PopoverBody color="white">
                <DarkMode>
                  <span>{ t(CONTRACT_VERIFICATION_FIELD_KEYS.method.popoverTitle, { count: methods.length }) }</span>
                  <OrderedList>
                    { methods.map(renderPopoverListItem) }
                  </OrderedList>
                </DarkMode>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      </Box>
      <FormFieldFancySelect<FormFields, 'method'>
        name="method"
        placeholder={ t(CONTRACT_VERIFICATION_FIELD_KEYS.method.placeholder) }
        options={ options }
        isRequired
        isAsync={ false }
        isReadOnly={ options.length === 1 }
      />
    </VStack>
  );
};

export default React.memo(ContractVerificationFieldMethod);
